import { useNativeNavigationViewContext } from "@eventr/native/navigation/react";
import { isTrailingStackId, useSplitStore } from "feat/nativeNavigation/split.store";
import type { ReactNode } from "react";
import { tabOfStack } from "../tab-bar/tab-stack-ids";
import { PANE_GROUP_IDS } from "../screen-layout";
import { PaneContext, type PaneSide } from "./pane-context";

/** The pane a view's stack is, under iOS native navigation, or null outside two panes. */
export function paneSideOfStack(stackId: string | undefined, twoPane: boolean): PaneSide | null {
  if (!twoPane || !stackId) return null;
  if (isTrailingStackId(stackId)) return "trailing";
  const tab = tabOfStack(stackId);
  return tab && PANE_GROUP_IDS.includes(tab) ? "leading" : null;
}

/**
 * Which pane this web view's screen is in under iOS native navigation: null outside a pane, which
 * makes a screen one-column. Safe to call on web, where the view context is empty.
 */
export function useNativePaneSide(): PaneSide | null {
  const { stack } = useNativeNavigationViewContext();
  // The splits, not this screen's route: a column's own first screen is no pane route.
  const twoPane = useSplitStore((s) => s.tabsAreSplits && !s.collapsed);
  return paneSideOfStack(stack, twoPane);
}

/**
 * Under iOS native navigation each pane is a native stack and each screen its own web view, so a
 * screen's pane is the stack it was pushed onto rather than where `PaneShell` placed it.
 */
export function NativePaneSide({ children }: { children: ReactNode }) {
  const pane = useNativePaneSide();
  return <PaneContext.Provider value={pane}>{children}</PaneContext.Provider>;
}
